import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Pencil,
  Save,
  Sparkles,
  Send,
  ExternalLink,
  Clock,
  Calendar,
  Users,
  Trash2,
  Plus,
  Download,
  CheckCircle2,
  Video,
  Loader2,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { formatInTz, userBrowserTimezone, COMMON_TIMEZONES } from "@/lib/time";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import TaskList from "@/components/tasks/TaskList";
import ContactAutocomplete from "@/components/ContactAutocomplete";
import TaskFormDialog from "@/components/tasks/TaskFormDialog";
import TaskDetailDrawer from "@/components/tasks/TaskDetailDrawer";
import AISuggestTasksDialog from "@/components/tasks/AISuggestTasksDialog";
import SummaryEditor, { SummaryView } from "@/components/SummaryEditor";
import RecallPanel, { RecallReportExportDialog } from "@/components/recall/RecallPanel";
import { initials } from "@/lib/taskHelpers";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function MeetingDetails() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [m, setM] = useState(null);
  const [summary, setSummary] = useState(null);
  const [followup, setFollowup] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [pendingChanges, setPendingChanges] = useState(null); // {payload, changedFields}
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [showSummaryAiSuggest, setShowSummaryAiSuggest] = useState(false);
  // Bumps every time the summary or follow-up gets edited elsewhere so the
  // RecallPanel re-fetches its aggregate record (keeps Recall ↔ AI summary
  // tab in sync without lifting the whole record into this page).
  const [recallReloadKey, setRecallReloadKey] = useState(0);
  // Controlled tab — so deep links like `#followup-summary` from the Recall
  // EDIT buttons can switch into the Follow-up tab AND scroll to the
  // section in one click.
  const initialTabFromHash = () => {
    const h = window.location.hash;
    if (h === "#recall") return "recall";
    if (h && h.startsWith("#followup")) return "followup";
    return "overview";
  };
  const [activeTab, setActiveTab] = useState(initialTabFromHash);
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      if (h && h.startsWith("#followup")) {
        setActiveTab("followup");
      } else if (h === "#recall") {
        setActiveTab("recall");
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const userTz = user?.timezone || userBrowserTimezone();

  // If we arrived here via "End for All", we set state.fromEndForAll = true so
  // the page's Back button skips browser history and lands on /app/meetings
  // instead of re-entering the dead meeting room.
  const cameFromEndForAll = !!location.state?.fromEndForAll;
  const handleBack = () => {
    if (cameFromEndForAll) {
      navigate("/app/meetings", { replace: true });
    } else {
      // Safer default than navigate(-1) — never re-enters the meeting room.
      navigate("/app/meetings");
    }
  };

  const load = async () => {
    try {
      const r = await api.get(`/meetings/${meetingId}`);
      setM(r.data);
      setDraft(r.data);
    } catch (e) {
      toast.error("Could not load meeting");
    }
    try {
      const s = await api.get(`/summaries/by-meeting/${meetingId}`);
      setSummary(s.data);
    } catch {}
    try {
      const f = await api.get(`/followups?workspace_id=${currentWorkspace?.workspace_id || ""}`);
      const mine = (f.data || []).find((x) => x.meeting_id === meetingId);
      setFollowup(mine || null);
    } catch {}
  };

  useEffect(() => {
    if (meetingId && currentWorkspace) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, currentWorkspace]);

  const copyLink = () => {
    const url = `${window.location.origin}/m/${m.public_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Meeting link copied");
  };
  const downloadIcs = () => {
    if (!m) return;
    const start = m.start_time?.replace(/[-:]/g, "").split(".")[0] + "Z";
    const end = m.end_time?.replace(/[-:]/g, "").split(".")[0] + "Z";
    const url = `${window.location.origin}/m/${m.public_id}`;
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Bloq//Meeting//EN\nBEGIN:VEVENT\nUID:${m.public_id}@bloq.video\nDTSTAMP:${start}\nDTSTART:${start}\nDTEND:${end}\nSUMMARY:${m.title}\nDESCRIPTION:${(m.description || "").replace(/\n/g, "\\n")}\\nJoin: ${url}\nURL:${url}\nLOCATION:${url}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${m.title.replace(/[^a-z0-9]/gi, "_")}.ics`;
    a.click();
  };

  // Build the diff of edited fields between the loaded meeting and the
  // current draft so we know what changed (and which to send/notify about).
  const buildEditPayload = () => {
    if (!m) return { payload: {}, changedFields: [] };
    const candidate = {
      title: draft.title,
      description: draft.description,
      start_time: draft.start_time,
      duration_minutes: draft.duration_minutes,
      timezone: draft.timezone,
      guest_access_enabled: draft.guest_access_enabled,
      waiting_room_enabled: draft.waiting_room_enabled,
      invited_emails: draft.invited_emails,
      ai_summary_enabled: draft.ai_summary_enabled,
      transcription_enabled: draft.transcription_enabled,
      recording_enabled: draft.recording_enabled,
    };
    const payload = {};
    const changedFields = [];
    for (const k of Object.keys(candidate)) {
      const next = candidate[k];
      if (next === undefined) continue;
      const prev = m[k];
      const same = Array.isArray(next)
        ? Array.isArray(prev) && next.length === prev.length && next.every((v, i) => v === prev[i])
        : (next ?? null) === (prev ?? null);
      if (!same) {
        payload[k] = next;
        changedFields.push(k);
      }
    }
    return { payload, changedFields };
  };

  const handleSaveClicked = () => {
    const { payload, changedFields } = buildEditPayload();
    if (changedFields.length === 0) {
      toast.info("No changes to save.");
      setEdit(false);
      return;
    }
    setPendingChanges({ payload, changedFields });
    setShowNotifyDialog(true);
  };

  const applySave = async ({ notify }) => {
    if (!pendingChanges) return;
    try {
      await api.patch(`/meetings/${meetingId}`, pendingChanges.payload, {
        params: { notify: notify ? "true" : "false" },
      });
      toast.success(notify ? "Meeting updated — notifications sent" : "Meeting updated");
      setShowNotifyDialog(false);
      setPendingChanges(null);
      setEdit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const cancelSave = () => {
    setShowNotifyDialog(false);
    setPendingChanges(null);
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/summaries/generate`, { meeting_id: meetingId });
      setSummary(data);
      setRecallReloadKey((k) => k + 1);
      toast.success("AI summary ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Summary generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveFollowup = async (patch) => {
    try {
      const { data } = await api.patch(`/followups/${meetingId}`, patch);
      setFollowup(data);
      toast.success("Follow-up saved");
    } catch { toast.error("Could not save follow-up"); }
  };

  const cancelMeeting = async () => {
    if (!window.confirm("Cancel this meeting? Attendees can be notified separately.")) return;
    await api.patch(`/meetings/${meetingId}`, { status: "canceled" });
    toast.success("Meeting canceled");
    load();
  };

  const deleteMeeting = async () => {
    if (!window.confirm("Permanently delete this meeting? This can't be undone.")) return;
    await api.delete(`/meetings/${meetingId}`);
    toast.success("Meeting deleted");
    navigate("/app/meetings");
  };

  if (!m) return <p className="text-sm text-[#82828c]">Loading meeting…</p>;

  return (
    <div className="flex flex-col gap-6" data-testid="meeting-details-page">
      <button onClick={handleBack} className="inline-flex items-center gap-2 text-sm text-[#4a4a52] hover:text-[#005e7a] w-fit" data-testid="meeting-back">
        <ArrowLeft className="w-4 h-4" /> Back to Meetings
      </button>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="bloq-eyebrow">{m.status}</p>
          {edit ? (
            <input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="bloq-input text-3xl bloq-headline mt-2" data-testid="edit-title" />
          ) : (
            <h1 className="bloq-headline text-4xl sm:text-5xl mt-2" data-testid="details-title">{m.title}</h1>
          )}
          <p className="text-sm text-[#82828c] mt-2 flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatInTz(m.start_time, userTz)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {m.duration_minutes}m</span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {m.invited_emails?.length || 0}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copyLink} className="bloq-btn-secondary inline-flex items-center gap-2" data-testid="details-copy-link"><Copy className="w-4 h-4" /> Copy link</button>
          <button onClick={downloadIcs} className="bloq-btn-secondary inline-flex items-center gap-2" data-testid="details-download-ics">Download .ics</button>
          {m.status !== "canceled" && m.status !== "ended" && (
            <Link to={`/m/${m.public_id}`} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="details-open-lobby">
              {m.status === "live" ? "Join" : "Open lobby"} <ExternalLink className="w-4 h-4" />
            </Link>
          )}
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-meeting-settings">Settings</TabsTrigger>
          <TabsTrigger value="recall" data-testid="tab-recall">Recall</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">Participants</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="recording" data-testid="tab-recording">Recording</TabsTrigger>
          <TabsTrigger value="followup" data-testid="tab-followup">Follow-up</TabsTrigger>
        </TabsList>

        <AISuggestTasksDialog
          open={showSummaryAiSuggest}
          onOpenChange={setShowSummaryAiSuggest}
          meetingId={meetingId}
          members={[]}
          onCreated={() => { /* meeting tasks tab will reload on next visit */ }}
        />

        <TabsContent value="overview" className="mt-5">
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="bloq-card p-6 lg:col-span-2">
              <p className="bloq-headline text-lg mb-3 text-[#005e7a]">Description</p>
              {edit ? (
                <textarea rows={5} className="bloq-input" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="edit-description" />
              ) : (
                <p className="text-sm text-[#4a4a52] whitespace-pre-wrap">{m.description || "No description yet."}</p>
              )}
              <div className="mt-5 pt-5 border-t border-[#e8e1d3] flex flex-col gap-2">
                <p className="bloq-headline text-lg text-[#005e7a]">Shareable link</p>
                <code className="text-xs bg-[#f5efe6] p-3 rounded-lg break-all" data-testid="details-shareable-link">{`${window.location.origin}/m/${m.public_id}`}</code>
                <p className="text-xs text-[#82828c]">Anyone with the link can {m.guest_access_enabled ? "join as a guest" : "join only if invited"}.</p>
              </div>
            </div>
            <div className="bloq-card p-6 flex flex-col gap-4">
              <p className="bloq-headline text-lg text-[#005e7a]">Invited</p>
              {m.invited_emails?.length ? (
                <ul className="text-sm text-[#4a4a52] flex flex-col gap-1">
                  {m.invited_emails.map((e) => <li key={e}>{e}</li>)}
                </ul>
              ) : (
                <p className="text-sm text-[#82828c]">No invitees — share the link to bring people in.</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="participants" className="mt-5">
          <ParticipantsTab meetingId={meetingId} />
        </TabsContent>

        <TabsContent value="recall" className="mt-5">
          <RecallPanel
            meetingId={meetingId}
            onChanged={load}
            reloadKey={recallReloadKey}
            onNavigateToFollowup={(anchorId) => {
              // Synchronously switch tab + scroll to the section once
              // FollowupCard has mounted. Using requestAnimationFrame so
              // we don't query the anchor before React renders it.
              setActiveTab("followup");
              window.history.replaceState(null, "", `#${anchorId}`);
              const tryScroll = (attempt = 0) => {
                const el = document.getElementById(anchorId);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                } else if (attempt < 20) {
                  setTimeout(() => tryScroll(attempt + 1), 50);
                }
              };
              requestAnimationFrame(() => tryScroll(0));
            }}
          />
        </TabsContent>

        <TabsContent value="tasks" className="mt-5">
          <MeetingTasksTab meetingId={meetingId} meeting={m} summary={summary} />
        </TabsContent>

        <TabsContent value="recording" className="mt-5">
          <RecordingTab meetingId={meetingId} />
        </TabsContent>

        <TabsContent value="followup" className="mt-5">
          <FollowupCard
            followup={followup}
            summary={summary}
            generating={generating}
            onGenerateSummary={generateSummary}
            onSummaryUpdated={(s) => { setSummary(s); setRecallReloadKey((k) => k + 1); }}
            onSuggestTasks={() => setShowSummaryAiSuggest(true)}
            onSave={async (patch) => { await saveFollowup(patch); setRecallReloadKey((k) => k + 1); }}
            meetingId={meetingId}
            meeting={m}
            onChanged={() => { load(); setRecallReloadKey((k) => k + 1); }}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-5">
          <div className="bloq-card p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="bloq-headline text-xl text-[#005e7a]">Meeting settings</p>
              {!edit && (
                <button onClick={() => { setEdit(true); setDraft({ ...m }); }} className="bloq-btn-secondary inline-flex items-center gap-2 self-start" data-testid="edit-meeting-button">
                  <Pencil className="w-4 h-4" /> Edit meeting
                </button>
              )}
            </div>
            {edit ? (
              <MeetingEditForm draft={draft} setDraft={setDraft} userTz={userTz} />
            ) : (
              <ReadOnlySettings m={m} userTz={userTz} />
            )}
            {edit && (
              <div className="flex flex-wrap gap-2 mt-2">
                <button onClick={handleSaveClicked} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="save-meeting-button">
                  <Save className="w-4 h-4" /> Save changes
                </button>
                <button onClick={() => { setEdit(false); setDraft({ ...m }); }} className="bloq-btn-secondary" data-testid="cancel-edit-meeting">Cancel</button>
              </div>
            )}
            <div className="bloq-divider my-2" />
            <p className="bloq-headline text-base">Danger zone</p>
            <div className="flex flex-wrap gap-2">
              {m.status !== "canceled" && m.status !== "ended" && (
                <button onClick={cancelMeeting} className="bloq-btn-secondary text-[#c43838]" data-testid="cancel-meeting-button">Cancel meeting</button>
              )}
              <button onClick={deleteMeeting} className="bloq-btn-secondary text-[#c43838] inline-flex items-center gap-2" data-testid="delete-meeting-button">
                <Trash2 className="w-4 h-4" /> Delete permanently
              </button>
            </div>
          </div>
          <NotifyChangesDialog
            open={showNotifyDialog}
            changedFields={pendingChanges?.changedFields || []}
            onSend={() => applySave({ notify: true })}
            onSilent={() => applySave({ notify: false })}
            onCancel={cancelSave}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ label, anchorId, children }) {
  return (
    <div id={anchorId} className="scroll-mt-24">
      <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium mb-2">{label}</p>
      {children}
    </div>
  );
}

// FollowupCard — structured action items editor + editable email draft + notes.
// Action items are stored as {item_id, title, status, source, ...} objects.
// Same data shown under Recall > Follow Up section.
// FollowupCard — single source of truth for action items, decisions, recap
// email, and follow-up notes. Includes the "Generate AI follow-up email"
// button and the "Send follow-up email with Recall Report attached" flow.
// FollowupCard — the single working surface for AI Summary, Action Items,
// Decisions, Notes from the Meeting, and the Recap Email Draft. The AI
// Summary used to live in its own tab but was merged here per Iter 17 spec.
function FollowupCard({
  followup, summary, generating, onGenerateSummary, onSummaryUpdated,
  onSuggestTasks, onSave, meetingId, meeting, onChanged,
}) {
  const [notes, setNotes] = useState(followup?.notes || "");
  const [items, setItems] = useState([]);
  const [subject, setSubject] = useState(followup?.email_subject || "");
  const [email, setEmail] = useState(followup?.email_draft || "");
  const [draftTitle, setDraftTitle] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [newDecision, setNewDecision] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);
  const [showRecallExport, setShowRecallExport] = useState(false);

  // AI Summary inline editor state — now a rich-text (HTML) field.
  const [editingSummary, setEditingSummary] = useState(false);
  const [draftSummary, setDraftSummary] = useState(summary?.summary_html || summary?.summary_text || "");
  const [savingSummary, setSavingSummary] = useState(false);
  useEffect(() => {
    setDraftSummary(summary?.summary_html || summary?.summary_text || "");
  }, [summary?.summary_html, summary?.summary_text]);

  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      const { data } = await api.patch(`/summaries/by-meeting/${meetingId}`, { summary_html: draftSummary });
      onSummaryUpdated?.(data);
      toast.success("Summary saved");
      setEditingSummary(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSavingSummary(false); }
  };
  const handleRegenerateSummary = async () => {
    if (summary?.edited_by_user) {
      const ok = window.confirm("Regenerating will overwrite your manual edits to the summary. Continue?");
      if (!ok) return;
    }
    onGenerateSummary?.();
  };
  const summaryEditedBadge = summary?.edited_by_user ? (
    <span className="text-[10px] uppercase tracking-widest bg-[#005e7a]/10 text-[#005e7a] px-1.5 py-0.5 rounded font-bold">edited</span>
  ) : null;

  useEffect(() => {
    setNotes(followup?.notes || "");
    setSubject(followup?.email_subject || "");
    setEmail(followup?.email_draft || "");
    setItems(
      (followup?.action_items || []).map((it) =>
        typeof it === "string"
          ? { item_id: `tmp_${Math.random().toString(36).slice(2, 8)}`, title: it, status: "todo", source: "manual" }
          : it
      )
    );
  }, [followup]);

  // Auto-focus on a section if the URL hash points to it (Recall tab EDIT
  // links use anchors like #followup-summary, #followup-actions, etc.).
  useEffect(() => {
    const h = window.location.hash;
    if (!h) return;
    const el = document.querySelector(h);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, []);

  // Load + reload decisions from the dedicated /decisions endpoint so they
  // are always in sync with AI re-generations.
  const loadDecisions = async () => {
    if (!meetingId) return;
    try {
      const { data } = await api.get(`/decisions/by-meeting/${meetingId}`);
      setDecisions(data);
    } catch {}
  };
  useEffect(() => { loadDecisions(); /* eslint-disable-next-line */ }, [meetingId]);

  const persist = (nextItems = items, extras = {}) => {
    onSave({ action_items: nextItems, ...extras });
  };
  const addItem = () => {
    const t = draftTitle.trim();
    if (!t) return;
    const next = [
      ...items,
      {
        item_id: `tmp_${Math.random().toString(36).slice(2, 8)}`,
        title: t,
        status: "todo",
        source: "manual",
        priority: "medium",
      },
    ];
    setItems(next);
    setDraftTitle("");
    persist(next);
  };
  const updateItem = (id, patch) => {
    const next = items.map((it) => (it.item_id === id ? { ...it, ...patch } : it));
    setItems(next);
    persist(next);
  };
  const removeItem = (id) => {
    const next = items.filter((it) => it.item_id !== id);
    setItems(next);
    persist(next);
  };
  const toggleComplete = (it) => {
    updateItem(it.item_id, { status: it.status === "done" ? "todo" : "done" });
  };
  const saveEmail = async () => {
    setSavingEmail(true);
    try { await onSave({ email_subject: subject, email_draft: email }); }
    finally { setSavingEmail(false); }
  };
  const saveNotes = () => onSave({ notes });

  // === Decisions ===
  const addDecision = async () => {
    const t = newDecision.trim();
    if (!t || !meetingId) return;
    try {
      const { data } = await api.post(`/decisions`, {
        meeting_id: meetingId,
        decision_text: t,
        source: "manual",
      });
      setDecisions((d) => [...d, data]);
      setNewDecision("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add decision");
    }
  };
  const updateDecision = async (id, text) => {
    try {
      const { data } = await api.patch(`/decisions/${id}`, { decision_text: text });
      setDecisions((arr) => arr.map((x) => (x.decision_id === id ? data : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not edit"); }
  };
  const deleteDecision = async (id) => {
    try {
      await api.delete(`/decisions/${id}`);
      setDecisions((arr) => arr.filter((x) => x.decision_id !== id));
    } catch { toast.error("Could not delete"); }
  };

  // === Generate / send recap email ===
  const generateEmail = async () => {
    setGeneratingEmail(true);
    try {
      const { data } = await api.post(`/followups/${meetingId}/generate-email`);
      setSubject(data.subject || data.email_subject || "");
      setEmail(data.body || data.email_draft || "");
      toast.success("AI recap email drafted");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate recap email");
    } finally { setGeneratingEmail(false); }
  };

  return (
    <div className="bloq-card p-6 flex flex-col gap-5" data-testid="followup-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="bloq-headline text-xl text-[#005e7a]">Recap & follow-up</p>
        <button
          onClick={() => setShowRecallExport(true)}
          className="bloq-btn-secondary text-xs inline-flex items-center gap-2"
          data-testid="followup-download-recall-report"
        >
          <Download className="w-3.5 h-3.5" /> Download Recall Report
        </button>
      </div>

      {/* 1. AI Summary — first, per spec */}
      <Section label="AI summary" anchorId="followup-summary">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#005e7a]" />
            <p className="text-sm font-medium">Generated from the meeting</p>
            {summaryEditedBadge}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onSuggestTasks} className="bloq-btn-secondary text-xs inline-flex items-center gap-2" data-testid="summary-ai-suggest-tasks-button">
              <Sparkles className="w-3.5 h-3.5" /> Suggest tasks
            </button>
            <button onClick={handleRegenerateSummary} disabled={generating} className="bloq-btn-primary text-xs" data-testid="generate-summary-button">
              {generating ? "Generating…" : summary?.status === "completed" ? "Regenerate" : "Generate"}
            </button>
            {!editingSummary && ((summary?.summary_text || summary?.summary_html) ? (
              <button onClick={() => setEditingSummary(true)} className="bloq-btn-secondary text-xs inline-flex items-center gap-1" data-testid="summary-edit-button">
                <Pencil className="w-3.5 h-3.5" /> Edit summary
              </button>
            ) : (
              <button onClick={() => { setDraftSummary(""); setEditingSummary(true); }} className="bloq-btn-secondary text-xs inline-flex items-center gap-1" data-testid="summary-add-button">
                <Pencil className="w-3.5 h-3.5" /> Add summary
              </button>
            ))}
          </div>
        </div>
        {editingSummary ? (
          <div className="flex flex-col gap-2">
            <SummaryEditor
              value={draftSummary}
              onChange={setDraftSummary}
              meetingId={meetingId}
              testId="summary-edit-input"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setEditingSummary(false); setDraftSummary(summary?.summary_html || summary?.summary_text || ""); }} className="bloq-btn-secondary text-xs" data-testid="summary-edit-cancel">Cancel</button>
              <button onClick={saveSummary} disabled={savingSummary} className="bloq-btn-primary text-xs inline-flex items-center gap-1" data-testid="summary-edit-save">
                <Save className="w-3.5 h-3.5" /> {savingSummary ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ) : (summary?.summary_text || summary?.summary_html) ? (
          <div>
            <SummaryView html={summary.summary_html || summary.summary_text} className="text-[#4a4a52]" />
          </div>
        ) : (
          <div>
            <p className="text-sm text-[#82828c]">
              No summary yet. Generate one from the meeting, or write your own.
            </p>
          </div>
        )}
      </Section>

      {/* 2. Action items */}
      <Section label={`Action items (${items.length})`} anchorId="followup-actions">
        <div className="flex flex-col gap-1.5 mb-3">
          {items.length === 0 && (
            <p className="text-sm text-[#82828c]">No action items yet. AI-suggested action items appear here automatically once you generate a summary.</p>
          )}
          {items.map((it) => (
            <ActionItemRow
              key={it.item_id}
              item={it}
              onChange={(patch) => updateItem(it.item_id, patch)}
              onToggle={() => toggleComplete(it)}
              onDelete={() => removeItem(it.item_id)}
            />
          ))}
        </div>
        <div className="flex gap-2 items-stretch">
          <input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            placeholder="Add an action item…"
            className="bloq-input flex-1"
            data-testid="followup-new-item-input"
          />
          <button onClick={addItem} className="bloq-btn-primary inline-flex items-center gap-1" data-testid="followup-add-item">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </Section>

      {/* 3. Decisions */}
      <Section label={`Decisions (${decisions.length})`} anchorId="followup-decisions">
        <div className="flex flex-col gap-1.5 mb-3" data-testid="decisions-list">
          {decisions.length === 0 && (
            <p className="text-sm text-[#82828c]">No decisions captured. AI-generated decisions appear here when you regenerate the summary.</p>
          )}
          {decisions.map((d) => (
            <DecisionRow
              key={d.decision_id}
              decision={d}
              onSave={(text) => updateDecision(d.decision_id, text)}
              onDelete={() => deleteDecision(d.decision_id)}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={newDecision}
            onChange={(e) => setNewDecision(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addDecision()}
            placeholder="Add a decision…"
            className="bloq-input flex-1"
            data-testid="decision-new-input"
          />
          <button onClick={addDecision} className="bloq-btn-primary inline-flex items-center gap-1" data-testid="decision-add-button">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </Section>

      {/* 4. Notes from the meeting */}
      <Section label="Notes from the meeting" anchorId="followup-notes">
        <textarea rows={4} className="bloq-input" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} data-testid="followup-notes-input" />
      </Section>

      {/* 5. Recap email draft */}
      <Section label="Recap email draft" anchorId="followup-email">
        <div className="flex flex-wrap gap-2 mb-2">
          <button
            onClick={generateEmail}
            disabled={generatingEmail}
            className="bloq-btn-secondary text-xs inline-flex items-center gap-1"
            data-testid="generate-followup-email-button"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {generatingEmail ? "Drafting…" : email ? "Regenerate with AI" : "Generate Follow-Up Email Draft"}
          </button>
        </div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Subject</label>
        <input
          className="bloq-input mt-1 mb-3"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="followup-subject-input"
          placeholder="AI generates a subject when you draft the email. You can edit it here."
        />
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Body</label>
        <textarea
          rows={8}
          className="bloq-input font-sans mt-1"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="followup-email-input"
          placeholder="Click Generate Follow-Up Email Draft above to draft with AI, or write your own."
        />
        <div className="flex gap-2 mt-2 flex-wrap">
          <button onClick={saveEmail} disabled={savingEmail} className="bloq-btn-primary text-xs inline-flex items-center gap-1" data-testid="followup-save-email">
            <Save className="w-3.5 h-3.5" /> {savingEmail ? "Saving…" : "Save draft"}
          </button>
          <button onClick={() => { navigator.clipboard.writeText(email || ""); toast.success("Recap email copied"); }} className="bloq-btn-secondary text-xs inline-flex items-center gap-1" data-testid="followup-copy-email">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button
            disabled={!email.trim()}
            onClick={() => setShowSendModal(true)}
            className="bloq-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="followup-send-button"
          >
            <Send className="w-3.5 h-3.5" /> Send follow-up email…
          </button>
        </div>
        <p className="text-[11px] text-[#82828c] mt-2">
          Sending uses your configured email provider. The Recall Report (PDF or DOCX) can
          be attached. Recipients are confirmed before sending.
        </p>
      </Section>

      <SendFollowupModal
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        meeting={meeting}
        emailSubject={subject}
        emailDraft={email}
        decisions={decisions}
        onSent={() => { setShowSendModal(false); onChanged?.(); }}
      />
      <RecallReportExportDialog
        open={showRecallExport}
        onClose={() => setShowRecallExport(false)}
        meetingId={meetingId}
        meetingTitle={meeting?.title}
        whenIso={meeting?.start_time}
        tz={meeting?.timezone}
      />
    </div>
  );
}

function DecisionRow({ decision, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(decision.decision_text || "");
  useEffect(() => { setDraft(decision.decision_text || ""); }, [decision.decision_text]);
  const pill = decision.source === "ai_generated" ? (
    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#005e7a]/10 text-[#005e7a]">AI</span>
  ) : (
    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#82828c]/15 text-[#82828c]">manual</span>
  );
  return (
    <div className="flex items-center gap-2 group p-2 rounded hover:bg-[#f5efe6]/60" data-testid={`decision-${decision.decision_id}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-[#005e7a] flex-shrink-0" />
      {editing ? (
        <>
          <input
            className="bloq-input flex-1 py-1 text-sm"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { if (draft.trim()) onSave(draft.trim()); setEditing(false); }
              if (e.key === "Escape") { setDraft(decision.decision_text || ""); setEditing(false); }
            }}
            data-testid={`decision-edit-${decision.decision_id}`}
          />
          <button onClick={() => { if (draft.trim()) onSave(draft.trim()); setEditing(false); }} className="bloq-btn-primary text-xs px-2 py-1"><Save className="w-3 h-3" /></button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm">{decision.decision_text}</span>
          {pill}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-white" title="Edit" data-testid={`decision-edit-btn-${decision.decision_id}`}>
              <Pencil className="w-3 h-3 text-[#82828c]" />
            </button>
            <button onClick={onDelete} className="p-1 rounded hover:bg-white" title="Delete" data-testid={`decision-delete-${decision.decision_id}`}>
              <Trash2 className="w-3 h-3 text-[#82828c]" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const SEND_SECTIONS = [
  { key: "meeting_details", label: "Meeting details", on: true },
  { key: "participants", label: "Participants", on: true },
  { key: "ai_summary", label: "Summary", on: true },
  { key: "action_items", label: "Action items", on: true },
  { key: "decisions", label: "Decisions", on: true },
  { key: "follow_up", label: "Follow Up notes", on: true },
  { key: "tasks", label: "Tasks", on: false },
  { key: "recording_references", label: "Recording references", on: false },
  { key: "activity_timeline", label: "Activity timeline", on: false },
  { key: "transcript", label: "Transcript (can be long)", on: false },
  { key: "notes", label: "Notes", on: false },
  { key: "follow_up_email_draft", label: "Follow-Up Email Draft", on: false },
];

function SendFollowupModal({ open, onClose, meeting, emailSubject, emailDraft, decisions, onSent }) {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState("");
  const [sections, setSections] = useState(SEND_SECTIONS.filter((s) => s.on).map((s) => s.key));
  const [format, setFormat] = useState("pdf");
  const [includeAttachment, setIncludeAttachment] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && meeting) {
      // Prefill with the invited attendees + the host's (current user's)
      // email, de-duplicated case-insensitively. Trailing ", " lets the
      // contact-autocomplete append the next pick cleanly.
      const seed = [...(meeting?.invited_emails || [])];
      if (user?.email) seed.push(user.email);
      const seen = new Set();
      const deduped = seed.filter((e) => {
        const k = (e || "").trim().toLowerCase();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setRecipients(deduped.length ? deduped.join(", ") + ", " : "");
    }
  }, [open, meeting, user]);

  const toggle = (k) => setSections((arr) => arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);
  const send = async () => {
    const list = recipients.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Pick at least one recipient");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/followups/${meeting.meeting_id}/send`, {
        recipients: list,
        subject: emailSubject,
        body: emailDraft,
        sections,
        attachment_format: format,
        include_attachment: includeAttachment,
      });
      if (data?.provider_not_configured > 0 && data?.sent === 0) {
        toast.error("Email provider isn't configured. Set EMAIL_PROVIDER + provider keys in backend/.env.");
      } else if (data?.failed > 0 && data?.sent === 0) {
        toast.error(`Provider rejected all recipients: ${(data.provider_errors || []).join("; ").slice(0, 200) || "unknown error"}`);
      } else if (data?.sent > 0) {
        toast.success(`Follow-up email sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}.`);
        onSent?.();
      } else {
        toast.error("No recipients could be sent. Check your provider config.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  if (!meeting) return null;
  const senderFrom = "hello@bloq.video"; // configured workspace sender
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="send-followup-modal">
        <DialogHeader>
          <DialogTitle>Send follow-up email</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="text-xs text-[#82828c] bg-[#f5efe6] rounded p-2">
            <strong>From:</strong> {senderFrom}
            {user?.email && <> · <strong>Reply-to:</strong> {user.email}</>}
            <div className="mt-1 text-[10px] text-[#82828c]">Verified workspace sender ensures the provider accepts the message; replies route back to you.</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Subject (from your draft)</label>
            <p className="text-sm mt-1 px-2 py-1.5 rounded bg-[#f5efe6]" data-testid="send-subject-display">
              {emailSubject || <span className="text-[#82828c] italic">(empty — set a subject in the draft above)</span>}
            </p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Recipients (type an email or pick a contact)</label>
            <ContactAutocomplete
              workspaceId={meeting?.workspace_id}
              value={recipients}
              queryOverride={(recipients.split(/[,;]+/).pop() || "").trim()}
              onChange={setRecipients}
              onSelect={(c) => {
                if (!c.email) return;
                const existing = (recipients || "")
                  .split(/[\s,;]+/)
                  .map((s) => s.trim().toLowerCase())
                  .filter(Boolean);
                if (existing.includes(c.email.toLowerCase())) return;
                const parts = (recipients || "").split(/(\s*[,;]\s*)/);
                if (parts.length === 0) { setRecipients(`${c.email}, `); return; }
                parts[parts.length - 1] = c.email;
                let result = parts.join("");
                if (!/[,;]\s*$/.test(result)) result += ", ";
                setRecipients(result);
              }}
              className="bloq-input mt-1 font-mono text-xs"
              placeholder="alice@example.com, bob@example.com"
              testid="send-recipients-input"
              multiline
            />
            <p className="text-[11px] text-[#82828c] mt-1">{decisions.length} decision{decisions.length === 1 ? "" : "s"} will be included if you've selected the Decisions section.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Recall Report sections to include</label>
              <label className="flex items-center gap-1 text-xs text-[#82828c]">
                <input
                  type="checkbox"
                  checked={includeAttachment}
                  onChange={(e) => setIncludeAttachment(e.target.checked)}
                  data-testid="send-include-attachment-toggle"
                />
                Attach Recall Report
              </label>
            </div>
            <div className={`flex flex-col gap-1 max-h-56 overflow-y-auto pr-1 ${includeAttachment ? "" : "opacity-50 pointer-events-none"}`}>
              {SEND_SECTIONS.map((s) => {
                const on = sections.includes(s.key);
                return (
                  <label key={s.key} className="flex items-center gap-2 py-1 px-1 rounded text-sm cursor-pointer hover:bg-[#f5efe6]">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(s.key)}
                      data-testid={`send-section-${s.key}`}
                    />
                    {s.label}
                  </label>
                );
              })}
            </div>
          </div>
          {includeAttachment && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Format</span>
              <label className="flex items-center gap-1">
                <input type="radio" checked={format === "pdf"} onChange={() => setFormat("pdf")} data-testid="send-format-pdf" /> PDF
              </label>
              <label className="flex items-center gap-1">
                <input type="radio" checked={format === "docx"} onChange={() => setFormat("docx")} data-testid="send-format-docx" /> DOCX
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary" disabled={sending} data-testid="send-cancel-button">Cancel</button>
          <button onClick={send} disabled={sending || !emailSubject?.trim()} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="send-confirm-button">
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionItemRow({ item, onChange, onToggle, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title || "");
  useEffect(() => { setDraft(item.title || ""); }, [item.title]);
  const sourcePill = item.source === "ai_generated" ? (
    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#005e7a]/10 text-[#005e7a]">AI</span>
  ) : (
    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#82828c]/15 text-[#82828c]">manual</span>
  );
  return (
    <div className="flex items-center gap-2 group p-2 rounded hover:bg-[#f5efe6]/60" data-testid={`action-item-${item.item_id}`}>
      <input
        type="checkbox"
        checked={item.status === "done"}
        onChange={onToggle}
        className="accent-[#005e7a] w-4 h-4 flex-shrink-0"
        data-testid={`action-item-toggle-${item.item_id}`}
      />
      {editing ? (
        <>
          <input
            className="bloq-input flex-1 py-1 text-sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") { onChange({ title: draft.trim() || item.title }); setEditing(false); }
              if (e.key === "Escape") { setDraft(item.title || ""); setEditing(false); }
            }}
            data-testid={`action-item-edit-${item.item_id}`}
          />
          <button onClick={() => { onChange({ title: draft.trim() || item.title }); setEditing(false); }} className="bloq-btn-primary text-xs px-2 py-1">
            <Save className="w-3 h-3" />
          </button>
        </>
      ) : (
        <>
          <span className={`flex-1 text-sm ${item.status === "done" ? "line-through text-[#82828c]" : "text-[#1a1a1f]"}`}>
            {item.title}
          </span>
          {sourcePill}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-white" title="Edit" data-testid={`action-item-edit-btn-${item.item_id}`}>
              <Pencil className="w-3 h-3 text-[#82828c]" />
            </button>
            <button onClick={onDelete} className="p-1 rounded hover:bg-white" title="Delete" data-testid={`action-item-delete-${item.item_id}`}>
              <Trash2 className="w-3 h-3 text-[#82828c]" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function ParticipantsTab({ meetingId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api
      .get(`/meetings/${meetingId}/participants`)
      .then((r) => setList(r.data))
      .catch(() => toast.error("Could not load participants"))
      .finally(() => setLoading(false));
  }, [meetingId]);
  if (loading) return <p className="text-sm text-[#82828c]">Loading…</p>;
  if (list.length === 0) {
    return (
      <div className="bloq-card p-10 text-center">
        <Users className="w-8 h-8 mx-auto text-[#82828c]" />
        <p className="bloq-headline text-lg mt-3">No one's joined yet.</p>
        <p className="text-sm text-[#82828c] mt-1">Participants will show up here once they enter the room.</p>
      </div>
    );
  }
  return (
    <div className="bloq-card p-2 overflow-x-auto" data-testid="participants-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
            <th className="px-3 py-3">Name</th>
            <th className="px-3 py-3">Email</th>
            <th className="px-3 py-3">Role</th>
            <th className="px-3 py-3">Joined</th>
            <th className="px-3 py-3">Left</th>
          </tr>
        </thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.participant_id} className="hover:bg-[#f5efe6]" data-testid={`participant-row-${p.participant_id}`}>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <Avatar className="w-7 h-7 rounded-md">
                    <AvatarImage src={p.avatar_url || ""} className="rounded-md" />
                    <AvatarFallback className="bg-[#005e7a] text-white text-[10px] rounded-md">{initials(p.name)}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{p.name}</span>
                </div>
              </td>
              <td className="px-3 py-3 text-[#4a4a52]">{p.email || "—"}</td>
              <td className="px-3 py-3"><span className="bloq-pill">{p.role}</span></td>
              <td className="px-3 py-3 text-[#82828c]">{p.joined_at ? new Date(p.joined_at).toLocaleString() : "—"}</td>
              <td className="px-3 py-3 text-[#82828c]">{p.left_at ? new Date(p.left_at).toLocaleString() : "Still in"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MeetingTasksTab({ meetingId, meeting, summary }) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showAiSuggest, setShowAiSuggest] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const load = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/tasks`, {
      params: { workspace_id: currentWorkspace.workspace_id, meeting_id: meetingId, include_subtasks: false },
    });
    setTasks(data);
  };
  useEffect(() => {
    load();
    if (currentWorkspace) {
      Promise.all([
        api.get(`/labels?workspace_id=${currentWorkspace.workspace_id}`),
        api.get(`/workspaces/${currentWorkspace.workspace_id}/members`),
      ]).then(([l, m]) => { setLabels(l.data); setMembers(m.data); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace, meetingId]);

  const updateTask = async (id, patch) => {
    const { data } = await api.patch(`/tasks/${id}`, patch);
    setTasks((prev) => prev.map((t) => (t.task_id === id ? data : t)));
  };
  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${id}`);
    setTasks((prev) => prev.filter((t) => t.task_id !== id));
  };
  const createFromSummary = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/tasks/from-summary`, { meeting_id: meetingId });
      toast.success(`Created ${data.created} tasks from action items`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create tasks");
    } finally {
      setGenerating(false);
    }
  };
  const hasAiActions = (summary?.action_items || []).length > 0;

  return (
    <div className="flex flex-col gap-4" data-testid="meeting-tasks-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="bloq-headline text-xl text-[#005e7a]">Tasks from this meeting</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowAiSuggest(true)} className="bloq-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="ai-suggest-tasks-button">
            <Sparkles className="w-3.5 h-3.5" /> Suggest tasks from meeting notes
          </button>
          {hasAiActions && (
            <button onClick={createFromSummary} disabled={generating} className="bloq-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="create-tasks-from-summary">
              <Sparkles className="w-3.5 h-3.5" /> {generating ? "Creating…" : "Create tasks from action items"}
            </button>
          )}
          <button onClick={() => setShowForm(true)} className="bloq-btn-primary inline-flex items-center gap-2 text-xs" data-testid="new-meeting-task-button">
            <Plus className="w-3.5 h-3.5" /> New task from meeting
          </button>
        </div>
      </div>
      {tasks.length === 0 ? (
        <div className="bloq-card p-8 text-center">
          <CheckCircle2 className="w-8 h-8 mx-auto text-[#82828c]" />
          <p className="bloq-headline text-lg mt-3">No action items yet.</p>
          <p className="text-sm text-[#82828c] mt-1">Add the next step before this meeting becomes a fond memory.</p>
        </div>
      ) : (
        <TaskList
          tasks={tasks}
          labels={labels}
          currentUser={user}
          onUpdate={updateTask}
          onDelete={deleteTask}
          onOpen={setDetailTaskId}
        />
      )}
      <TaskFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        labels={labels}
        members={members}
        defaults={{ related_meeting_id: meetingId }}
        onCreated={() => { setShowForm(false); load(); }}
      />
      <AISuggestTasksDialog
        open={showAiSuggest}
        onOpenChange={setShowAiSuggest}
        meetingId={meetingId}
        members={members}
        onCreated={load}
      />
      <TaskDetailDrawer
        taskId={detailTaskId}
        onClose={() => setDetailTaskId(null)}
        labels={labels}
        members={members}
        onUpdated={load}
        onDeleted={load}
      />
    </div>
  );
}

function RecordingTab({ meetingId }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meetings/${meetingId}/recordings`);
      setRecs(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meetingId]);

  const anyProcessing = recs.some((r) => r.status === "processing");
  // While a recording is still uploading to storage, quietly re-check every
  // 20s so it flips to a downloadable row on its own without a manual refresh.
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(() => { load(); }, 20000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [anyProcessing, meetingId]);

  const checkAgain = async () => {
    setChecking(true);
    try {
      await api.post(`/meetings/${meetingId}/recording/reconcile`);
      await load();
      toast.success("Checked storage for finished recordings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check recording status");
    } finally {
      setChecking(false);
    }
  };

  const download = async (rec) => {
    try {
      // For Agora Cloud Recording rows we ask the backend for a time-
      // limited signed URL and then open it directly. This is the only
      // way Google Cloud Storage will serve a private object under
      // uniform bucket-level access.
      if (rec.provider === "agora_cloud_recording") {
        const { data } = await api.get(
          `/meetings/${meetingId}/recording/${rec.recording_id}/download-url`
        );
        if (!data?.signed_url) throw new Error("No signed URL");
        // Open in a new tab — the browser handles the redirect to GCS
        // and the Content-Disposition header forces a download.
        window.open(data.signed_url, "_blank", "noopener,noreferrer");
        return;
      }
      // Legacy browser-MediaRecorder recordings still come from Emergent
      // storage; download as a Blob the old way.
      const r = await api.get(
        `/meetings/recordings/${rec.recording_id}/download`,
        { responseType: "blob" }
      );
      // Prefer the canonical filename the server sets via Content-Disposition.
      // Blob downloads bypass the browser's normal CD handling so we have to
      // parse it ourselves; falls back to the stored filename / a generic name.
      let downloadName = rec.filename || `recording-${rec.recording_id}.webm`;
      const cd = r.headers?.["content-disposition"] || r.headers?.["Content-Disposition"];
      if (cd) {
        // Prefer RFC 5987 (filename*=UTF-8''...) when present.
        const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
        if (star) {
          try { downloadName = decodeURIComponent(star[1].trim().replace(/^"|"$/g, "")); }
          catch { /* fall through */ }
        } else {
          const plain = /filename="?([^"\\;]+)"?/i.exec(cd);
          if (plain) downloadName = plain[1].trim();
        }
      }
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };
  return (
    <div className="bloq-card p-6 flex flex-col gap-4" data-testid="recording-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-[#005e7a]" />
          <p className="bloq-headline text-xl text-[#005e7a]">Meeting recordings</p>
        </div>
        <button
          onClick={checkAgain}
          disabled={checking}
          className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="recording-check-again-btn"
        >
          {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Check again
        </button>
      </div>
      <p className="text-sm text-[#4a4a52]">
        Recordings are captured inside the meeting room (host clicks Record). Cloud recordings upload to storage after the meeting ends, so a fresh recording may show as <span className="font-medium">Processing</span> for a few minutes before it’s ready to download.
      </p>
      {loading ? (
        <p className="text-sm text-[#82828c]">Loading…</p>
      ) : recs.length === 0 ? (
        <div className="bg-[#f5efe6] rounded-lg p-6 text-center">
          <p className="text-sm font-medium">No recordings yet.</p>
          <p className="text-xs text-[#82828c] mt-1">Start recording from the meeting room (host control) and it'll show up here when you stop.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {recs.map((r) => {
            const isProcessing = r.status === "processing";
            const isFailed = r.status === "failed";
            return (
            <li key={r.recording_id} className="bloq-card-soft p-3 flex items-center justify-between gap-3" data-testid={`recording-${r.recording_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate flex items-center gap-2">
                  {isProcessing && (
                    <span className="inline-flex items-center gap-1 text-amber-600 shrink-0" data-testid={`recording-processing-${r.recording_id}`}>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing…
                    </span>
                  )}
                  {isFailed && (
                    <span className="inline-flex items-center gap-1 text-red-600 shrink-0" data-testid={`recording-failed-${r.recording_id}`}>
                      <AlertCircle className="w-3.5 h-3.5" /> Not available
                    </span>
                  )}
                  {!isProcessing && !isFailed && (r.filename || "recording.mp4")}
                </p>
                <p className="text-xs text-[#82828c]">
                  {isProcessing
                    ? "Uploading to storage — this can take a few minutes after the meeting ends."
                    : isFailed
                    ? (r.error_message || "No file was produced for this recording.")
                    : (
                      <>
                        By {r.started_by_name || r.uploaded_by_name}
                        {r.started_by_role === "external_cohost" && (
                          <span className="ml-1 text-teal-400">(external co-host)</span>
                        )}
                        {r.started_by_role === "cohost" && (
                          <span className="ml-1 text-teal-400">(co-host)</span>
                        )}
                        {r.started_by_role === "host" && (
                          <span className="ml-1 text-amber-400">(host)</span>
                        )}
                        {" · "}{new Date(r.created_at).toLocaleString()} · {((r.size_bytes || 0) / 1024 / 1024).toFixed(1)} MB
                      </>
                    )}
                </p>
              </div>
              {isProcessing ? (
                <button onClick={checkAgain} disabled={checking} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60" data-testid={`recording-recheck-${r.recording_id}`}>
                  {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Check
                </button>
              ) : isFailed ? null : (
                <button onClick={() => download(r)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 shrink-0" data-testid={`download-recording-${r.recording_id}`}>
                  <Download className="w-3 h-3" /> Download
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Helpers used by the Settings tab
// ─────────────────────────────────────────────────────────────────────────────
function MeetingEditForm({ draft, setDraft, userTz }) {
  const { currentWorkspace } = useWorkspace();
  // Source of truth for the Invited-emails input is a RAW STRING so that
  // separators (comma, space) typed by the user don't get eaten by
  // immediate array normalization. draft.invited_emails (array) is kept
  // in sync via updateInvites so buildEditPayload still serializes the
  // right shape for the backend. Seeded once from the meeting's current
  // invited_emails; the form unmounts on save/cancel so this stays fresh.
  const [invitesRaw, setInvitesRaw] = useState(
    () => (draft.invited_emails || []).join(", ")
  );
  // start_time is stored as full ISO. UI splits to date + time inputs in the
  // user's local timezone for ease of use, then re-composes on change.
  const isoToLocalParts = (iso) => {
    if (!iso) return { date: "", time: "" };
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      };
    } catch { return { date: "", time: "" }; }
  };
  const parts = isoToLocalParts(draft.start_time);
  const setDate = (newDate) => {
    if (!newDate) return;
    const t = parts.time || "09:00";
    const iso = new Date(`${newDate}T${t}`).toISOString();
    setDraft((d) => ({ ...d, start_time: iso }));
  };
  const setTime = (newTime) => {
    if (!newTime) return;
    const dt = parts.date || isoToLocalParts(new Date().toISOString()).date;
    const iso = new Date(`${dt}T${newTime}`).toISOString();
    setDraft((d) => ({ ...d, start_time: iso }));
  };
  // Update both the raw display string AND the parsed array atomically so
  // buildEditPayload always sees a clean array even mid-typing.
  const updateInvites = (raw) => {
    setInvitesRaw(raw);
    setDraft((d) => ({
      ...d,
      invited_emails: raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean),
    }));
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Title</label>
        <input
          className="bloq-input mt-1"
          value={draft.title || ""}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          data-testid="edit-title"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Description</label>
        <textarea
          rows={4}
          className="bloq-input mt-1"
          value={draft.description || ""}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          data-testid="edit-description"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Date</label>
        <input
          type="date"
          className="bloq-input mt-1"
          value={parts.date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="edit-date"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Start time</label>
        <input
          type="time"
          className="bloq-input mt-1"
          value={parts.time}
          onChange={(e) => setTime(e.target.value)}
          data-testid="edit-time"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Duration (min)</label>
        <input
          type="number"
          min={5}
          className="bloq-input mt-1"
          value={draft.duration_minutes || 30}
          onChange={(e) => setDraft((d) => ({ ...d, duration_minutes: Number(e.target.value) }))}
          data-testid="edit-duration"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Timezone</label>
        <Select value={draft.timezone || userTz} onValueChange={(v) => setDraft((d) => ({ ...d, timezone: v }))}>
          <SelectTrigger className="mt-1" data-testid="edit-timezone-trigger"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-60">
            {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Invited emails (comma- or space-separated)</label>
        <ContactAutocomplete
          workspaceId={currentWorkspace?.workspace_id}
          value={invitesRaw}
          queryOverride={(invitesRaw.split(/[,;]+/).pop() || "").trim()}
          onChange={updateInvites}
          onSelect={(c) => {
            if (!c.email) return;
            const existing = (invitesRaw || "")
              .split(/[\s,;]+/)
              .map((s) => s.trim().toLowerCase())
              .filter(Boolean);
            if (existing.includes(c.email.toLowerCase())) return;
            const parts = (invitesRaw || "").split(/(\s*[,;]\s*)/);
            if (parts.length === 0) { updateInvites(`${c.email}, `); return; }
            parts[parts.length - 1] = c.email;
            let result = parts.join("");
            if (!/[,;]\s*$/.test(result)) result += ", ";
            updateInvites(result);
          }}
          placeholder="alex@acme.com, ben@acme.com"
          testid="edit-invited-emails"
        />
      </div>
      <div className="sm:col-span-2 flex flex-col gap-2">
        <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium mt-2">Access &amp; recall</p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!draft.guest_access_enabled}
            onChange={(e) => setDraft((d) => ({ ...d, guest_access_enabled: e.target.checked }))}
            className="accent-[#005e7a] w-4 h-4"
            data-testid="edit-guest-access"
          />
          Allow guest access (anyone with the link can join)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!draft.waiting_room_enabled}
            onChange={(e) => setDraft((d) => ({ ...d, waiting_room_enabled: e.target.checked }))}
            className="accent-[#005e7a] w-4 h-4"
            data-testid="edit-waiting-room"
          />
          Enable waiting room
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.ai_summary_enabled !== false}
            onChange={(e) => setDraft((d) => ({ ...d, ai_summary_enabled: e.target.checked }))}
            className="accent-[#005e7a] w-4 h-4"
            data-testid="edit-ai-summary-enabled"
          />
          AI summary on after meeting
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!draft.transcription_enabled}
            onChange={(e) => setDraft((d) => ({ ...d, transcription_enabled: e.target.checked }))}
            className="accent-[#005e7a] w-4 h-4"
            data-testid="edit-transcription-enabled"
          />
          Live transcription on (browser-based; captures local microphone only)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!draft.recording_enabled}
            onChange={(e) => setDraft((d) => ({ ...d, recording_enabled: e.target.checked }))}
            className="accent-[#005e7a] w-4 h-4"
            data-testid="edit-recording-enabled"
          />
          Recording enabled (host can start/stop in the meeting room)
        </label>
      </div>
    </div>
  );
}

function ReadOnlySettings({ m, userTz }) {
  const Row = ({ label, value }) => (
    <div className="flex flex-col gap-1 py-2 border-b border-[#1a1a1f]/06 last:border-0">
      <span className="text-[11px] uppercase tracking-widest text-[#82828c]">{label}</span>
      <span className="text-sm text-[#1a1a1f] break-words whitespace-pre-wrap">{value || "—"}</span>
    </div>
  );
  return (
    <div className="flex flex-col">
      <Row label="Title" value={m.title} />
      <Row label="Description" value={m.description} />
      <Row label="Start" value={formatInTz(m.start_time, m.timezone || userTz)} />
      <Row label="Duration" value={`${m.duration_minutes || 0} min`} />
      <Row label="Timezone" value={m.timezone || userTz} />
      <Row label="Invited" value={(m.invited_emails || []).join(", ")} />
      <Row label="Guest access" value={m.guest_access_enabled ? "Allowed" : "Invite-only"} />
      <Row label="Waiting room" value={m.waiting_room_enabled ? "On" : "Off"} />
      <Row label="AI summary" value={m.ai_summary_enabled !== false ? "On" : "Off"} />
      <Row label="Transcription" value={m.transcription_enabled ? "On" : "Off"} />
      <Row label="Recording" value={m.recording_enabled ? "On" : "Off"} />
    </div>
  );
}

function NotifyChangesDialog({ open, changedFields, onSend, onSilent, onCancel }) {
  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="meeting-notify-dialog">
        <DialogHeader>
          <DialogTitle>Send notification of change to other participants?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#4a4a52]">
          You changed: <span className="font-medium text-[#1a1a1f]">{(changedFields || []).join(", ") || "(nothing)"}</span>.
          Choose how to roll it out.
        </p>
        <DialogFooter className="gap-2">
          <button onClick={onCancel} className="bloq-btn-secondary" data-testid="notify-cancel">Cancel</button>
          <button onClick={onSilent} className="bloq-btn-secondary" data-testid="notify-silent">Don't send</button>
          <button onClick={onSend} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="notify-send">
            <Send className="w-4 h-4" /> Send notification
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
